import { useEffect, useState } from "react";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Checkbox,
  FormControl,
  Paper,
  CircularProgress,
} from "@mui/material";

import { Box } from "@mui/system";
import DataStatusAlert from "../UI_HelpersOf_DataAccuracy/DataStatusAlert_Helpers";
import DoneIcon from "@mui/icons-material/Done";
import { useSelector, useDispatch } from "react-redux";
import {
  selectIsLoading,
  selectUserToken,
  setDisplayErrorSnackBar,
  setIsLoading,
} from "../../../../../features/userSlice";
import store from "../../../../../store";
import { DataAccuracyEndPointLogic } from "../../../../../logic/AssetManagement/Health/Data Accuracy/dataAccuracyEndPointLogic";
import SimpleBackdrop from "../../../../BackDrop/BackDrop";
import {
  setIsSuccess,
  setReloadKey,
} from "../../../../../features/dataAccuracySlice";

const ResolveDataAccuracy = ({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) => {
  const { selectedRowsData, isSuccess } = useSelector(
    (state: any) => state.dataAccuracy
  );

  const [reason, setReason] = useState<string>("");
  const [selected, setSelected] = useState<any[]>([]);
  const userToken = useSelector(selectUserToken);
  const loading = useSelector(selectIsLoading);
  const dispatch = useDispatch();

  useEffect(() => {
    if (open) {
      setSelected(selectedRowsData || []);
    }
  }, [open, selectedRowsData]);

  const isAllSelected =
    selected.length > 0 && selected.length === selectedRowsData.length;

  const handleSelectRow = (row: any) => {
    setSelected((prev) =>
      prev.some((r) => r === row)
        ? prev.filter((r) => r !== row)
        : [...prev, row]
    );
  };

  const handleSelectAll = () => {
    setSelected(isAllSelected ? [] : [...selectedRowsData]);
  };

  const resolveBody = {
    tasks: selected.map((task) => {
      const taskObj: any = {
        shared_uid: task.shared_uid,
        current_status: "Done",
      };

      if (reason?.trim()) {
        taskObj.reason = reason.trim();
      }

      return taskObj;
    }),
  };

  const clearLocalState = () => {
    setSelected([]);
    setReason("");
  };

  const callAPIs = async () => {
    if (selected.length > 0) {
      store.dispatch(setIsLoading(true));

      try {
        await DataAccuracyEndPointLogic.updateDataAccuracyTask(
          userToken,
          resolveBody,
          dispatch
        );

        clearLocalState();
        store.dispatch(setReloadKey(true));
        store.dispatch(
          setDisplayErrorSnackBar({
            message: "Resolve Success",
            type: "success",
          })
        );

        onClose();
      } catch (error) {
        console.error("Resolve API error:", error);
        store.dispatch(
          setDisplayErrorSnackBar({
            message: "Resolve failed. Please try again.",
            type: "error",
          })
        );
      } finally {
        store.dispatch(setIsLoading(false));
      }
    } else {
      store.dispatch(
        setDisplayErrorSnackBar({
          message: "Please select tasks and provide a reason.",
          type: "error",
        })
      );
    }
  };

  useEffect(() => {
    if (isSuccess) {
      dispatch(setIsSuccess(false));
    }
  }, [isSuccess, dispatch]);

  return (
    <Dialog
      open={open}
      onClose={() => {}}
      disableEscapeKeyDown
      maxWidth="md"
      fullWidth
    >
      {loading && <SimpleBackdrop />}
      <DialogTitle>Resolve Tasks</DialogTitle>
      <DialogContent>
        <Typography gutterBottom sx={{ paddingBottom: "2px" }}>
          Selected Tasks
        </Typography>
        <Paper sx={{ width: "100%", overflowY: "auto", height: "225px" }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox" sx={{ padding: "4px" }}>
                  <Checkbox
                    size="small"
                    color="secondary"
                    checked={isAllSelected}
                    onChange={handleSelectAll}
                  />
                </TableCell>
                <TableCell sx={{ padding: "4px" }}>Task Name</TableCell>
                <TableCell sx={{ padding: "4px" }}>Asset</TableCell>
                <TableCell sx={{ padding: "4px" }}>Brand</TableCell>
                <TableCell sx={{ padding: "4px" }}>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {selectedRowsData.map((row: any, index: number) => (
                <TableRow key={index}>
                  <TableCell padding="checkbox" sx={{ padding: "4px" }}>
                    <Checkbox
                      size="small"
                      color="secondary"
                      checked={selected.some((r) => r === row)}
                      onChange={() => handleSelectRow(row)}
                    />
                  </TableCell>
                  <TableCell sx={{ padding: "4px" }}>
                    {row.task_structure__title}
                  </TableCell>
                  <TableCell sx={{ padding: "4px" }}>
                    {row?.data["meta_data:website_name"]?.value}
                  </TableCell>
                  <TableCell sx={{ padding: "4px" }}>
                    {row?.data["query_output:brand_name"]?.value}
                  </TableCell>
                  <TableCell align="left" sx={{ padding: "4px" }}>
                    <DataStatusAlert status={row.current_status} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>

        <Box sx={{ paddingTop: 2 }}>
          <FormControl fullWidth margin="normal">
            <TextField
              label="Reason"
              color="secondary"
              placeholder="Enter reason for resolving"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              InputLabelProps={{ shrink: true }}
              size="small"
            />
          </FormControl>
        </Box>

        <Typography
          gutterBottom
          sx={{ fontSize: "12px", paddingBottom: "2px", color: "#666666" }}
        >
          All fields are mandatory
        </Typography>
      </DialogContent>
      <DialogActions sx={{ marginBottom: "16px", paddingRight: "24px" }}>
        <Button
          onClick={() => {
            if (!loading) {
              clearLocalState();
              onClose();
            }
          }}
          color="secondary"
          disabled={loading}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          color="secondary"
          sx={{ whiteSpace: "nowrap" }}
          onClick={callAPIs}
          disabled={loading || selected.length === 0}
          data-tracker="168"
        >
          {loading ? (
            <CircularProgress size={20} sx={{ color: "#fff" }} />
          ) : (
            <>
              Resolve
              <DoneIcon
                fontSize="small"
                sx={{ color: "#fff", marginLeft: "4px" }}
              />
            </>
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ResolveDataAccuracy;
